<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import CaptainMediaAPI from 'dashboard/api/captain/media';

const { t } = useI18n();
const route = useRoute();

const assistantId = computed(() => Number(route.params.assistantId));
const backUrl = computed(() => ({
  name: 'captain_assistants_settings_index',
  params: {
    accountId: route.params.accountId,
    assistantId: assistantId.value,
  },
}));

const isFetching = ref(false);
const isUploading = ref(false);
const mediaItems = ref([]);

const newKey = ref('');
const newCaption = ref('');
const newFile = ref(null);

const fetchMedia = async () => {
  isFetching.value = true;
  try {
    const { data } = await CaptainMediaAPI.getMedia(assistantId.value);
    mediaItems.value = data.payload || [];
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.MEDIA.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const onFileChange = event => {
  const [file] = event.target.files;
  newFile.value = file || null;
};

const canUpload = computed(
  () => newKey.value.trim() && newFile.value && !isUploading.value
);

const addMedia = async () => {
  if (!canUpload.value) return;
  isUploading.value = true;
  try {
    await CaptainMediaAPI.create(assistantId.value, {
      key: newKey.value.trim(),
      caption: newCaption.value.trim(),
      file: newFile.value,
    });
    newKey.value = '';
    newCaption.value = '';
    newFile.value = null;
    await fetchMedia();
    useAlert(t('CAPTAIN.ASSISTANTS.MEDIA.API.ADD_SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.MEDIA.API.ADD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const deleteMedia = async id => {
  try {
    await CaptainMediaAPI.delete(assistantId.value, id);
    mediaItems.value = mediaItems.value.filter(item => item.id !== id);
    useAlert(t('CAPTAIN.ASSISTANTS.MEDIA.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN.ASSISTANTS.MEDIA.API.DELETE_ERROR'));
  }
};

onMounted(fetchMedia);
</script>

<template>
  <PageLayout
    :header-title="$t('CAPTAIN.ASSISTANTS.MEDIA.TITLE')"
    :is-fetching="isFetching"
    :back-url="backUrl"
    :show-know-more="false"
    :show-pagination-footer="false"
    :show-assistant-switcher="false"
  >
    <template #body>
      <SettingsHeader
        :heading="$t('CAPTAIN.ASSISTANTS.MEDIA.TITLE')"
        :description="$t('CAPTAIN.ASSISTANTS.MEDIA.DESCRIPTION')"
      />
      <div class="flex flex-col gap-4 mt-7">
        <div
          class="flex flex-col gap-3 p-4 rounded-2xl bg-n-solid-2 outline outline-1 outline-n-container"
        >
          <Input
            v-model="newKey"
            :label="$t('CAPTAIN.ASSISTANTS.MEDIA.FORM.KEY.LABEL')"
            :placeholder="$t('CAPTAIN.ASSISTANTS.MEDIA.FORM.KEY.PLACEHOLDER')"
          />
          <Input
            v-model="newCaption"
            :label="$t('CAPTAIN.ASSISTANTS.MEDIA.FORM.CAPTION.LABEL')"
            :placeholder="
              $t('CAPTAIN.ASSISTANTS.MEDIA.FORM.CAPTION.PLACEHOLDER')
            "
          />
          <div class="flex flex-col gap-2">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('CAPTAIN.ASSISTANTS.MEDIA.FORM.FILE.LABEL') }}
            </label>
            <input
              type="file"
              accept="image/*"
              class="text-sm text-n-slate-12"
              @change="onFileChange"
            />
          </div>
          <div>
            <Button
              :label="$t('CAPTAIN.ASSISTANTS.MEDIA.FORM.UPLOAD')"
              :disabled="!canUpload"
              @click="addMedia"
            />
          </div>
        </div>

        <div v-if="mediaItems.length === 0" class="mt-1 mb-2">
          <span class="text-n-slate-11 text-sm">
            {{ $t('CAPTAIN.ASSISTANTS.MEDIA.EMPTY_MESSAGE') }}
          </span>
        </div>
        <div v-else class="flex flex-col gap-2">
          <div
            v-for="item in mediaItems"
            :key="item.id"
            class="flex items-center gap-3 p-3 rounded-xl bg-n-solid-2 outline outline-1 outline-n-container"
          >
            <img
              v-if="item.file_url"
              :src="item.file_url"
              :alt="item.key"
              class="object-cover w-12 h-12 rounded-lg"
            />
            <div class="flex flex-col flex-1 min-w-0">
              <span class="text-sm font-medium text-n-slate-12">
                {{ item.key }}
              </span>
              <span
                v-if="item.caption"
                class="text-sm text-n-slate-11 line-clamp-1"
              >
                {{ item.caption }}
              </span>
            </div>
            <Button
              icon="i-lucide-trash"
              color="ruby"
              ghost
              xs
              @click="deleteMedia(item.id)"
            />
          </div>
        </div>
      </div>
    </template>
  </PageLayout>
</template>
